import { Row, Col, message, Table, Button, Popconfirm, Tag, Tooltip } from "antd";
import { ColumnsType } from "antd/lib/table";
import { AppPageHeader } from "components/AppPageHeader";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MdDeleteOutline, MdHistory } from "react-icons/md";
import { BsPlus } from "react-icons/bs";
import { IWarehousePurchaseCode } from "common/interface/ISkuPurchaseCode";
import {
  getPurchaseCodeAmendments,
  getPurchaseCodes,
  submitPurchaseCodeAmendment,
} from "store/purchase-codes/purchaseCodesSlice";
import { AmendmentHistoryModal } from "components/AmendmentHistoryModal/AmendmentHistoryModal";
import { routes } from "common/routes";
import { AppActionStrip } from "components/AppActionStrip/AppActionStrip";
import { AppFilter, AppFilterButton, AppPurchaseCodesFilter } from "components/AppFilter";

const MESSAGE_KEY = "purchase_codes";

const SkuImage = styled.img`
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
`;

const StyledActionButton = styled.button`
  border: 1px solid;
  padding: 0.2rem;
  border-radius: 50%;
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  cursor: pointer;
  color: inherit;
`;

const PurchaseCodesPage = () => {
  useDocumentTitle("Purchase Codes");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [filterState, setFilterState] = useState(false);
  const { purchaseCodes, status, error, amendments, amendmentStatus } = useAppSelector(
    (state) => state.purchaseCodes
  );
  const [historyModal, setHistoryModal] = useState<{ id: string; name: string } | null>(null);

  const getData = useCallback(() => {
    dispatch(getPurchaseCodes());
  }, [dispatch]);

  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => {
    if (status === "loading") {
      message.loading({ content: "Loading Purchase Codes", key: MESSAGE_KEY });
    } else if (status === "error") {
      message.error({ content: error, key: MESSAGE_KEY });
    } else {
      message.destroy(MESSAGE_KEY);
    }

    return () => {
      message.destroy(MESSAGE_KEY);
    };
  }, [status, error]);

  const handleDelete = async (skuCode: string) => {
    const result = await dispatch(
      submitPurchaseCodeAmendment({
        entityType: "PURCHASE_SKU",
        entityId: skuCode,
        requestType: "DELETE",
        payload: { skuCode },
      })
    );
    if (submitPurchaseCodeAmendment.fulfilled.match(result)) {
      message.success({ content: "Delete request submitted for approval!", key: MESSAGE_KEY });
      getData();
    } else {
      const error = (result.payload as { message?: string })?.message;
      message.error({ content: error ?? "Something went wrong!", key: MESSAGE_KEY });
    }
  };

  const columns: ColumnsType<IWarehousePurchaseCode> = [
    {
      key: "image",
      title: "Image",
      dataIndex: "image",
      width: 60,
      render: (url: string, record) =>
        url ? <SkuImage src={url} alt={record.name} /> : <>-</>,
    },
    {
      key: "purchaseSkuCode",
      title: "Purchase Sku Code",
      dataIndex: "skuCode",
    },
    {
      key: "name",
      title: "Name",
      dataIndex: "name",
    },
    {
      key: "category",
      title: "Category",
      dataIndex: "category",
    },
    {
      key: "companyTag",
      title: "Company Tag",
      render: (_, record) => <>{record.metadata?.companyTag ?? "-"}</>,
    },
    {
      key: "unit_of_measurement",
      title: "Unit",
      dataIndex: "unit_of_measurement",
    },
    {
      key: "parentSkuCode",
      title: "Parent SKU",
      dataIndex: "parentSkuCode",
      render: (val: string | null) => <>{val ?? "-"}</>,
    },
    {
      key: "actions",
      title: "Actions",
      render: (_, record) => {
        const hasPendingAmendment = !!record.pendingAmendmentId;
        return (
          <>
            <Row align="middle" gutter={8} style={{ display: "flex", flexWrap: "nowrap" }}>
              <Col>
                <Popconfirm
                  title={`Submit a request to delete "${record.skuCode}"? It will be deleted once approved.`}
                  okText="Submit Request"
                  okButtonProps={{ danger: true }}
                  cancelText="Cancel"
                  onConfirm={() => handleDelete(record.skuCode)}
                  disabled={amendmentStatus === "saving" || hasPendingAmendment}
                >
                  <Tooltip
                    title={
                      hasPendingAmendment
                        ? `Pending request #${record.pendingAmendmentId}`
                        : undefined
                    }
                  >
                    <Button
                      shape="circle"
                      size="small"
                      danger
                      icon={<MdDeleteOutline size={16} />}
                      disabled={amendmentStatus === "saving" || hasPendingAmendment}
                    />
                  </Tooltip>
                </Popconfirm>
              </Col>
              <Col>
                <Tooltip title="View amendment history">
                  <StyledActionButton
                    type="button"
                    onClick={() =>
                      setHistoryModal({ id: record.skuCode, name: record.name ?? "" })
                    }
                  >
                    <MdHistory />
                  </StyledActionButton>
                </Tooltip>
              </Col>
            </Row>
            {hasPendingAmendment && (
              <Tag color="gold" style={{ marginTop: 6 }}>
                Pending #{record.pendingAmendmentId}
              </Tag>
            )}
          </>
        );
      },
    },
  ];

  const fetchHistoryPage = useCallback(
    (pageNo: number, pageSize: number) => {
      if (!historyModal) return;
      dispatch(getPurchaseCodeAmendments({ pageNo, pageSize, entityId: historyModal.id }));
    },
    [dispatch, historyModal]
  );

  const filterCount = useMemo(
    () => ["skuCode", "name"].filter((k) => searchParams.get(k)).length,
    [searchParams]
  );

  const rows = useMemo(() => {
    const all = purchaseCodes ?? [];
    const skuQuery = searchParams.get("skuCode")?.toLowerCase() ?? "";
    const nameQuery = searchParams.get("name")?.toLowerCase() ?? "";
    if (!skuQuery && !nameQuery) return all;
    return all.filter((code) => {
      const matchesSku = !skuQuery || code.skuCode?.toLowerCase().includes(skuQuery);
      const matchesName = !nameQuery || code.name?.toLowerCase().includes(nameQuery);
      return matchesSku && matchesName;
    });
  }, [purchaseCodes, searchParams]);

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Purchase Codes" />
        </Col>
      </Row>
      <Row>
        <AppActionStrip>
          <div className="action-strip__item">
            <AppFilterButton
              active={filterState}
              onClick={() => setFilterState((s) => !s)}
              filterCount={filterCount}
            />
          </div>
          <Button
            type="primary"
            icon={<BsPlus style={{ marginRight: "0.3rem" }} />}
            onClick={() => navigate(`/${routes.PO.CREATE_PURCHASE_CODE}`)}
          >
            Create New
          </Button>
        </AppActionStrip>
      </Row>
      <Row style={{ marginBottom: "1rem" }}>
        <AppFilter expanded={filterState}>
          <AppPurchaseCodesFilter />
        </AppFilter>
      </Row>
      {rows.length > 0 ? (
        <Row>
          <Col xs={24}>
            <Table<IWarehousePurchaseCode>
              columns={columns}
              dataSource={rows}
              rowKey="id"
              scroll={{ x: "100vh", y: "65vh" }}
              size="small"
              pagination={false}
              loading={status === "loading"}
              bordered
            />
          </Col>
        </Row>
      ) : (
        status !== "loading" && <>No Purchase Codes to show.</>
      )}
      <AmendmentHistoryModal
        title={`Amendment History${historyModal?.name ? ` — ${historyModal.name}` : ""} (#${historyModal?.id})`}
        open={!!historyModal}
        onClose={() => setHistoryModal(null)}
        amendments={amendments}
        loading={amendmentStatus === "loading"}
        fetchPage={fetchHistoryPage}
        detailPath={(amendmentId) =>
          `/${routes.PO.PURCHASE_CODE_AMENDMENT_DETAIL.replace(":id", String(amendmentId))}`
        }
      />
    </>
  );
};

export default PurchaseCodesPage;
